import { Input, Form, Button } from "antd";
import { FormComponentProps } from "antd/lib/form";
import React, { FC, useEffect, useState } from "react";
import { useApi } from "src/Api/useApi";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";
import { AdminPermissions } from "src/common/Permissions";
import { ModalOverlay, Page, Spinner } from "src/components/common";
import styles from "./OrganizationDatasetsBilling.module.scss";
import { toast } from "react-toastify";
import BillingVerification from "./OrganizationDatasetsBillingVerification";
import { BillingSubscription } from "src/@types/ImpiricaPlatformApi";

interface IProps extends FormComponentProps {
	match?: any;
}

const OrganizationDatasetsBilling: FC<IProps> = ({ match, form }) => {
	const { billingClient } = useApi();
	const [loading, setLoading] = useState<boolean>(true);
	const [disableSave, setDisableSave] = useState<boolean>(false);
	const [initialSubscriptionId, setInitialSubscriptionId] = useState<number>();
	const [billingVerificationOpen, setBillingVerificationOpen] =
		useState<boolean>(false);
	const datasetId = Number(match.params.datasetId);
	const { getFieldValue, getFieldDecorator } = form;
	const [subscriptionInfo, setSubscriptionInfo] =
		useState<BillingSubscription>();
	const [infoConfirmed, setInfoConfirmed] = useState<boolean>(false);
	const [disableVerify, setDisableVerify] = useState<boolean>(false);
	const [confirmRemoveModalOpen, setConfirmRemoveModalOpen] =
		useState<boolean>(false);

	useEffect(() => {
		loadData();
	}, [initialSubscriptionId]);

	useEffect(() => {
		setDisableSave(!infoConfirmed);
	}, [infoConfirmed]);

	const loadData = async () => {
		setLoading(true);
		try {
			if (billingClient) {
				const subscriptionId = await billingClient.getMaxioSubscriptionId(
					datasetId
				);
				subscriptionId && setInitialSubscriptionId(subscriptionId);
			}
		} catch (e) {
			setLoading(false);
		}
		setLoading(false);
	};

	const onSave = async (maxioSubscriptionId: number) => {
		if (!billingClient) throw new Error("No billing client");
		try {
			await billingClient.updateMaxioSubscriptionId(
				datasetId,
				maxioSubscriptionId
			);
			toast.success("Successfully saved billing information");
		} catch (e) {
			toast.error("Unable to save dataset information");
		}
	};

	const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setDisableSave(true);
		const fieldValue = getFieldValue("maxioSubscriptionId");
		await onSave(fieldValue);
		setTimeout(() => setDisableSave(false), 1500);
	};

	const onVerify = async () => {
		try {
			if (billingClient) {
				const response = await billingClient.getSubscription(
					getFieldValue("maxioSubscriptionId")
				);
				if (response) {
					setSubscriptionInfo(response);
					setBillingVerificationOpen(true);
				}
			}
		} catch (error) {
			toast.error("Unable to find subscription");
		}
	};

	const onVerifyClick = async () => {
		setDisableVerify(true);
		await onVerify();
		setTimeout(() => setDisableVerify(false), 1500);
	};

	const onConfirm = () => {
		setInfoConfirmed(true);
		setBillingVerificationOpen(false);
	};

	const onRemoveSubscriptionId = async () => {
		if (!billingClient) throw new Error("No billing client");
		try {
			await billingClient.removeMaxioSubscriptionId(
				datasetId,
				getFieldValue("maxioSubscriptionId")
			);
			setInitialSubscriptionId(undefined);
			setConfirmRemoveModalOpen(false);
			toast.success("Successfully removed billing information");
		} catch (e) {
			toast.error("Unable to remove dataset information");
		}
	};

	return loading ? (
		<Spinner />
	) : (
		<Page title="Dataset Billing">
			<AuthorizationWrapper
				permissions={[AdminPermissions.EditDataset]}
				type={"disable"}
			>
				<Form onSubmit={onSubmit} onChange={() => setInfoConfirmed(false)}>
					<div className="col">
						<p className={styles.label}>Maxio subscription Id</p>
						<div className={styles.row}>
							<Form.Item className={styles.input}>
								{getFieldDecorator("maxioSubscriptionId", {
									initialValue: initialSubscriptionId,
									rules: [
										{
											required: true,
											message: "Please enter the Maxio subscription Id",
										},
										{
											pattern: new RegExp(/^\d+$/),
											message: "Subscription Id must be a number",
										},
									],
								})(<Input placeholder="Enter the Maxio subscription Id" />)}
							</Form.Item>
							<Form.Item>
								<Button
									data-testid="verify-billing-info-button"
									type="primary"
									onClick={() => setConfirmRemoveModalOpen(true)}
									disabled={!getFieldValue("maxioSubscriptionId")}
								>
									Remove
								</Button>
							</Form.Item>
							<Form.Item>
								<Button
									data-testid="verify-billing-info-button"
									type="primary"
									onClick={onVerifyClick}
									disabled={disableVerify}
								>
									Verify
								</Button>
							</Form.Item>
						</div>
						<div className="row">
							<div className="col">
								<Form.Item>
									<Button
										data-testid="save-billing-id-button"
										type="primary"
										htmlType="submit"
										className="login-form-button"
										disabled={disableSave}
									>
										Save
									</Button>
								</Form.Item>
							</div>
						</div>
					</div>
					<BillingVerification
						open={billingVerificationOpen}
						subscriptionInfo={subscriptionInfo}
						onConfirm={onConfirm}
						onCancel={() => setBillingVerificationOpen(false)}
						loading={loading}
					/>
				</Form>
			</AuthorizationWrapper>
			<ModalOverlay
				isOpen={confirmRemoveModalOpen}
				modalSize="modal-md"
				headerChildren="Remove Subscription Id Confirmation"
				children="Are you sure you want to remove this subscription id?"
				confirmButtonChildren="Continue"
				confirmButtonAction={onRemoveSubscriptionId}
				cancelButtonChildren="Cancel"
				cancelButtonAction={() => setConfirmRemoveModalOpen(false)}
				onRequestClose={() => setConfirmRemoveModalOpen(false)}
			/>
		</Page>
	);
};

export default Form.create<IProps>()(OrganizationDatasetsBilling);
